import { MRT_ColumnDef, MRT_PaginationState } from "material-react-table";
import { useState, useMemo } from "react";
import { Box, Button, Chip, IconButton, Typography, Tooltip, FormControl, InputLabel, Select, MenuItem, TextField } from "@mui/material";
import { Delete, Edit, Add, Email, Phone, Language, LocationOn, Download, FileDownload, Clear } from "@mui/icons-material";
import { DataTable } from "../../libs/core/components/data-table";
import { useGetCSOMembersQuery, useDeleteCSOMemberMutation, CSOMembersFilterParams } from "./api/csoApi";
import { CSOMember } from "./types/cso";
import { ConfirmModal } from "../../libs/ui/modal/confirm_modal";
import AddEditCSO from "./components/AddEditCSO";

const CSOManager: React.FC = () => {
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const [openDialog, setOpenDialog] = useState(false);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [selectedCSO, setSelectedCSO] = useState<CSOMember | null>(null);
  const [filters, setFilters] = useState<CSOMembersFilterParams>({
    thematic_area: '',
    location: '',
  });

  // Build filter params - only include non-empty values
  const filterParams = useMemo(() => {
    const params: CSOMembersFilterParams = {};
    if (filters.thematic_area && filters.thematic_area.trim()) {
      params.thematic_area = filters.thematic_area;
    }
    if (filters.location && filters.location.trim()) {
      params.location = filters.location;
    }
    return Object.keys(params).length > 0 ? params : undefined;
  }, [filters]);

  const { data: csoMembers, isLoading, isError, isFetching } = useGetCSOMembersQuery(filterParams);
  const [deleteCSOMember] = useDeleteCSOMemberMutation();

  // Get unique thematic areas and locations for filters
  const thematicAreas = useMemo(() => {
    if (!csoMembers?.data) return [];
    const areas = new Set<string>();
    csoMembers.data.forEach((member: CSOMember) => {
      if (member.thematic_areas) {
        member.thematic_areas.forEach((area: string) => areas.add(area));
      }
    });
    return Array.from(areas).sort();
  }, [csoMembers]);

  const locations = useMemo(() => {
    if (!csoMembers?.data) return [];
    const locs = new Set<string>();
    csoMembers.data.forEach((member: CSOMember) => {
      if (member.location) {
        locs.add(member.location);
      }
    });
    return Array.from(locs).sort();
  }, [csoMembers]);

  // Filter data - use server-side filtering when filters are applied
  const filteredData = useMemo(() => {
    if (!csoMembers?.data) return [];
    // If we have filters, the data is already filtered by the backend
    if (filters.thematic_area || filters.location) {
      return csoMembers.data;
    }
    return csoMembers.data;
  }, [csoMembers, filters]);

  const handleExportXLSX = () => {
    const data = filteredData.length > 0 ? filteredData : (csoMembers?.data || []);
    
    // Create CSV content
    const headers = ['Organization Name', 'Location', 'Focal Person Name', 'Focal Person Phone', 'Email', 'Website', 'Thematic Areas', 'Description'];
    const rows = data.map((member: CSOMember) => [
      member.name || '',
      member.location || '',
      member.focal_person_name || '',
      member.focal_person_phone || '',
      member.email || '',
      member.website || '',
      (member.thematic_areas || []).join('; '),
      (member.description || '').replace(/\n/g, ' ').substring(0, 200),
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((row: (string | number)[]) => row.map((cell: string | number) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    // Create blob and download
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `cso_members_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const columns: Array<MRT_ColumnDef<CSOMember>> = [
    {
      header: "Organization Name",
      accessorKey: "name",
      size: 200,
      Cell({ row: { original } }) {
        return (
          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
            {original.name || 'Untitled Organization'}
          </Typography>
        );
      },
    },
    {
      header: "Location",
      accessorKey: "location",
      size: 150,
      Cell({ row: { original } }) {
        return (
          <Box display="flex" alignItems="center" gap={0.5}>
            <LocationOn fontSize="small" color="action" />
            <Typography variant="body2">
              {original.location || 'Not specified'}
            </Typography>
          </Box>
        );
      },
    },
    {
      header: "Focal Person",
      accessorKey: "focal_person_name",
      size: 150,
      Cell({ row: { original } }) {
        return (
          <Box>
            <Typography variant="body2" fontWeight="medium">
              {original.focal_person_name || 'Not specified'}
            </Typography>
            {original.focal_person_phone && (
              <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
                <Phone fontSize="small" color="action" />
                <Typography variant="caption" color="text.secondary">
                  {original.focal_person_phone}
                </Typography>
              </Box>
            )}
          </Box>
        );
      },
    },
    {
      header: "Contact",
      accessorKey: "email",
      size: 150,
      Cell({ row: { original } }) {
        return (
          <Box>
            {original.email && (
              <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                <Email fontSize="small" color="action" />
                <Typography variant="caption" color="text.secondary">
                  {original.email}
                </Typography>
              </Box>
            )}
            {original.website && (
              <Box display="flex" alignItems="center" gap={0.5}>
                <Language fontSize="small" color="action" />
                <Typography 
                  variant="caption" 
                  color="primary"
                  sx={{ 
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    '&:hover': { textDecoration: 'none' }
                  }}
                  onClick={() => window.open(original.website, '_blank')}
                >
                  Website
                </Typography>
              </Box>
            )}
          </Box>
        );
      },
    },
    {
      header: "Thematic Areas",
      accessorKey: "thematic_areas",
      size: 200,
      Cell({ row: { original } }) {
        const areas = original.thematic_areas || [];
        return (
          <Box display="flex" flexWrap="wrap" gap={0.5}>
            {areas.slice(0, 2).map((area, index) => (
              <Chip
                key={index}
                label={area}
                size="small"
                variant="outlined"
                color="primary"
              />
            ))}
            {areas.length > 2 && (
              <Chip
                label={`+${areas.length - 2} more`}
                size="small"
                variant="outlined"
                color="default"
              />
            )}
            {areas.length === 0 && (
              <Typography variant="caption" color="text.secondary">
                No areas specified
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      header: "Description",
      accessorKey: "description",
      size: 250,
      Cell({ row: { original } }) {
        const description = original.description || "";
        return (
          <Typography 
            variant="body2" 
            sx={{ 
              maxWidth: 200, 
              overflow: 'hidden', 
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: (description && description !== 'null') ? 'text.primary' : 'text.disabled'
            }}
          >
            {(description && description !== 'null') ? description : 'No description provided'}
          </Typography>
        );
      },
    },
    {
      header: "Documents",
      id: "documents",
      size: 200,
      Cell({ row: { original } }) {
        const documents = [];
        if (original.filled_xlsx_url) {
          documents.push({ name: 'Filled XLSX', url: original.filled_xlsx_url, icon: '📊' });
        }
        if (original.registration_certificate_url) {
          documents.push({ name: 'Registration Certificate', url: original.registration_certificate_url, icon: '📄' });
        }
        if (original.rules_regulation_doc_url) {
          documents.push({ name: 'Rules & Regulation', url: original.rules_regulation_doc_url, icon: '📋' });
        }
        if (original.license_url) {
          documents.push({ name: 'License', url: original.license_url, icon: '📜' });
        }

        if (documents.length === 0) {
          return (
            <Typography variant="caption" color="text.secondary">
              No documents
            </Typography>
          );
        }

        return (
          <Box display="flex" flexDirection="column" gap={0.5}>
            {documents.map((doc, index) => (
              <Tooltip key={index} title={doc.name}>
                <Box
                  component="a"
                  href={doc.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    textDecoration: 'none',
                    color: 'primary.main',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  <Download fontSize="small" />
                  <Typography variant="caption" sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {doc.name}
                  </Typography>
                </Box>
              </Tooltip>
            ))}
          </Box>
        );
      },
    },
  ];

  const handleDelete = async () => {
    if (selectedCSO) {
      try {
        await deleteCSOMember(selectedCSO.id.toString()).unwrap();
        setOpenConfirmModal(false);
        setSelectedCSO(null);
      } catch (error) {
        console.error("Failed to delete CSO member:", error);
      }
    }
  };

  const handleSuccess = () => {
    setSelectedCSO(null);
  };

  return (
    <Box>
      {/* Filters */}
      <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <FormControl sx={{ minWidth: 200 }} size="small">
          <InputLabel>Thematic Area</InputLabel>
          <Select
            value={filters.thematic_area || ''}
            label="Thematic Area"
            onChange={(e) => setFilters({ ...filters, thematic_area: e.target.value })}
          >
            <MenuItem value="">All Areas</MenuItem>
            {thematicAreas.map((area) => (
              <MenuItem key={area} value={area}>{area}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Location"
          size="small"
          value={filters.location || ''}
          onChange={(e) => setFilters({ ...filters, location: e.target.value })}
          placeholder="Search location..."
          sx={{ minWidth: 200 }}
        />

        {(filters.thematic_area || filters.location) && (
          <Button
            size="small"
            startIcon={<Clear />}
            onClick={() => setFilters({ thematic_area: '', location: '' })}
          >
            Clear Filters
          </Button>
        )}

        <Button
          variant="outlined"
          startIcon={<FileDownload />}
          onClick={handleExportXLSX}
          sx={{ ml: 'auto' }}
        >
          Export XLSX
        </Button>
      </Box>

      <DataTable<CSOMember>
        toolbar={
          <Box display="flex" alignItems="center" width="100%">
            <Box display="flex" gap={3} alignItems="center" flex="1">
              <Typography variant="h6">CSO Members</Typography>
              <Chip
                color="primary"
                variant="outlined"
                label={`${filteredData.length} Members`}
              />
            </Box>
            <Button
              onClick={() => {
                setSelectedCSO(null);
                setOpenDialog(true);
              }}
              sx={{ minWidth: "200px" }}
              variant="contained"
              startIcon={<Add />}
            >
              Add New CSO Member
            </Button>
          </Box>
        }
        list={filteredData}
        count={filteredData.length}
        columns={columns}
        getRowId={(row) => row?.id?.toString()}
        isLoading={isLoading}
        isSuccess={!isError}
        isError={isError}
        isFetching={isFetching}
        renderRowActions={(row) => (
          <Box display={"flex"} gap={1}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedCSO(row);
                setOpenConfirmModal(true);
              }}
              color="error"
            >
              <Delete />
            </IconButton>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedCSO(row);
                setOpenDialog(true);
              }}
              color="primary"
            >
              <Edit />
            </IconButton>
          </Box>
        )}
        pagination={pagination}
        onPaginationChange={setPagination}
      />

      <AddEditCSO
        open={openDialog}
        setModalOpen={setOpenDialog}
        onSuccess={handleSuccess}
        csoMember={selectedCSO}
      />

      {selectedCSO && (
        <ConfirmModal
          type="warning"
          title="Delete CSO Member"
          message={`Are you sure you want to delete '${selectedCSO.name}'?`}
          modalOpen={openConfirmModal}
          setModalOpen={setOpenConfirmModal}
          onOk={handleDelete}
        />
      )}
    </Box>
  );
};

export default CSOManager;
